import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Users, Download, TrendingUp, UserCheck, Clock, Car, Search,
  LogIn, MailCheck, KeyRound, Ban, RotateCcw, Loader2,
  Activity, AlertOctagon, Trash2, Zap, UserX, Share2, Copy, QrCode, Facebook, Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REF_TAG = 'henry';
const SHARE_URL = `https://www.fleetcost.app/?ref=${REF_TAG}`;
const SHARE_MESSAGE =
  "Running a fleet of bakkies, trucks or forklifts? FleetCost.App tracks every rand per km across your whole fleet — fuel, tyres, service, licences. Try it free for 3 months: ";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const PendingVerifyCard = ({ rows, onResend, onVerify, busyKey }) => {
  const pending = rows.filter((r) => !r.email_verified && !r.suspended);
  if (!pending.length) return null;
  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="pending-verify-card">
      <CardContent className="p-5">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
          <MailCheck className="w-4 h-4 text-amber-600" /> Waiting to verify ({pending.length})
        </h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">
          New owners can already use the app; these haven't confirmed their email yet. Resend the code or verify them yourself.
        </p>
        <div className="divide-y divide-amber-100">
          {pending.map((r) => {
            const v = r.verification || {};
            const status = v.last_send_ok === false
              ? <span className="text-red-700 font-medium" title={v.last_send_error || ''}>Send failed</span>
              : v.expired ? <span className="text-amber-700">Code expired</span>
                : v.has_code ? <span className="text-emerald-700">Sent</span> : <span className="text-slate-400">No code</span>;
            return (
              <div key={r.id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`pending-verify-row-${r.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-medium text-slate-900">{r.email}</p>
                  <p className="text-xs text-slate-500">{r.company_name || r.name} · signed up {fmtWhen(r.created_at)}</p>
                </div>
                <div className="text-xs w-44">
                  {status}<span className="text-slate-400"> · last code {fmtWhen(v.last_sent_at)}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => onResend(r)} disabled={busyKey === `${r.id}:resend-verification`}
                  data-testid={`pending-resend-${r.id}`}>
                  {busyKey === `${r.id}:resend-verification` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Resend code'}
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onVerify(r)} disabled={busyKey === `${r.id}:verify`}
                  data-testid={`pending-verify-${r.id}`}>
                  {busyKey === `${r.id}:verify` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify manually'}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const ShareLinkCard = ({ referredTotal, referred30d }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success('Share link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the link manually');
    }
  };

  const encoded = encodeURIComponent(SHARE_URL);
  const encodedMsg = encodeURIComponent(SHARE_MESSAGE + SHARE_URL);

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=10&data=${encoded}`;

  const downloadQr = async () => {
    try {
      const r = await fetch(qrSrc);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleetcost-share-qr-${REF_TAG}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('QR download failed');
    }
  };

  return (
    <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white" data-testid="share-link-card">
      <CardContent className="p-5 flex flex-col md:flex-row gap-5">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-indigo-600" />
            <h3 className="font-heading font-bold text-slate-900">Your sales link</h3>
            <span className="ml-auto text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
              <span className="font-semibold text-emerald-600">{referredTotal}</span> signups via your link
              {referred30d > 0 && <span className="text-slate-400"> · {referred30d} in last 30 d</span>}
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Paste this anywhere — WhatsApp, email, Facebook, printed flyers. Every tenant who signs up through it is tagged.
          </p>

          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={SHARE_URL}
              className="flex-1 min-w-0 text-sm font-mono px-3 py-2 bg-white border border-slate-200 rounded-md"
              onFocus={(e) => e.target.select()}
              data-testid="share-link-input"
            />
            <Button onClick={copy} variant="outline" data-testid="share-copy-btn">
              <Copy className="w-4 h-4 mr-1.5" /> {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={`https://wa.me/?text=${encodedMsg}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-[#25D366] text-white hover:bg-[#1EBE57]"
              data-testid="share-whatsapp-btn"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.555-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.864 9.864 0 001.44 5.21l-.591 2.159 2.21-.63z"/></svg>
              WhatsApp
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent('FleetCost.App — track every rand per km')}&body=${encodedMsg}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-slate-800 text-white hover:bg-slate-900"
              data-testid="share-email-btn"
            >
              <Mail className="w-4 h-4" /> Email
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-[#1877F2] text-white hover:bg-[#145dbf]"
              data-testid="share-facebook-btn"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
            <a
              href="https://www.facebook.com/fleetcostapp"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              data-testid="share-fb-page-btn"
            >
              <Facebook className="w-4 h-4" /> Our Page
            </a>
          </div>
        </div>

        <div className="flex flex-col items-center md:border-l md:border-slate-200 md:pl-5">
          <img
            src={qrSrc}
            alt="QR code for FleetCost.App share link"
            width={160} height={160}
            className="rounded-md border border-slate-200 bg-white"
            data-testid="share-qr"
          />
          <Button onClick={downloadQr} size="sm" variant="outline" className="mt-2" data-testid="share-qr-download">
            <QrCode className="w-3.5 h-3.5 mr-1" /> Download QR
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

const SubscribersPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ col: 'created_at', dir: 'desc' });
  const [actingRow, setActingRow] = useState(null); // `${id}:${action}` while an action is in-flight

  const reload = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/platform-admin/subscribers`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load subscribers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? data.rows.filter((r) =>
          (r.email || '').toLowerCase().includes(needle) ||
          (r.company_name || '').toLowerCase().includes(needle) ||
          (r.name || '').toLowerCase().includes(needle),
        )
      : data.rows;
    const sign = sort.dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = a[sort.col] ?? ''; const bv = b[sort.col] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
      return String(av).localeCompare(String(bv)) * sign;
    });
  }, [data, q, sort]);

  const toggleSort = (col) => setSort((s) => s.col === col
    ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' }
    : { col, dir: 'desc' });

  const exportCsv = () => {
    const headers = ['Email', 'Name', 'Company', 'Phone', 'Signed Up', 'Verified', 'Suspended', 'Vehicles', 'Team', 'Last Activity'];
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      const cells = [
        r.email, r.name || '', r.company_name || '', r.phone || '',
        fmtDate(r.created_at), r.email_verified ? 'yes' : 'pending',
        r.suspended ? 'yes' : 'no',
        r.vehicles, r.team_size, fmtDate(r.last_activity),
      ].map((c) => `"${String(c).replace(/"/g, '""')}"`);
      lines.push(cells.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleetcost-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doAction = async (row, action, { confirm: confirmMsg, successMsg } = {}) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActingRow(`${row.id}:${action}`);
    try {
      const r = await axios.post(`${API_URL}/api/platform-admin/subscribers/${row.id}/${action}`);
      if (r.data?.sent === false) toast.error(`Email could not be sent to ${row.email}: ${r.data.error || 'unknown error'}`);
      else if (successMsg !== null) toast.success(successMsg || 'Done');
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Action failed: ${action}`);
      return null;
    } finally {
      setActingRow(null);
    }
  };

  const impersonate = async (row) => {
    if (!window.confirm(`Log in as ${row.email}? You can click "Return to admin" at any time.`)) return;
    const result = await doAction(row, 'impersonate', { successMsg: `Now viewing as ${row.email}` });
    if (result?.ok) {
      // The server has swapped our auth cookie; just reload into the dashboard.
      window.location.href = '/dashboard';
    }
  };

  const resendVerify = (row) => doAction(row, 'resend-verification', {
    successMsg: `Verification email sent to ${row.email}`,
  });

  const resetPassword = (row) => doAction(row, 'reset-password', {
    confirm: `Email a password-reset code to ${row.email}?`,
    successMsg: `Password-reset email sent to ${row.email}`,
  }).then(reload);

  const suspend = (row) => doAction(row, 'suspend', {
    confirm: `Suspend ${row.email}? They won't be able to log in until reactivated.`,
    successMsg: `${row.email} suspended`,
  }).then(reload);

  const reactivate = (row) => doAction(row, 'reactivate', {
    successMsg: `${row.email} reactivated`,
  }).then(reload);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toLowerCase() !== (deleteTarget.email || '').toLowerCase()) {
      toast.error("Email doesn't match — type it exactly to confirm");
      return;
    }
    setDeleting(true);
    try {
      const r = await axios.delete(
        `${API_URL}/api/platform-admin/subscribers/${deleteTarget.id}`,
        { params: { confirm: deleteConfirm.trim() } }
      );
      toast.success(`Deleted ${deleteTarget.email}`, {
        description: `Purged ${Object.keys(r.data.deleted || {}).length} collections`,
      });
      setDeleteTarget(null);
      setDeleteConfirm('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading subscribers…</div>;
  if (!data) return <div className="p-8 text-center text-rose-500">Could not load subscribers.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Subscribers</h1>
          <p className="text-sm text-slate-500 mt-1">Every tenant that has signed up on FleetCost.App.</p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="export-subscribers-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Shareable marketing link — paste anywhere, every signup tagged `ref=henry` is tracked */}
      <ShareLinkCard referredTotal={data.referred_total ?? 0} referred30d={data.referred_last_30d ?? 0} />

      <PendingVerifyCard
        rows={data.rows || []}
        busyKey={actingRow}
        onResend={async (row) => {
          await doAction(row, 'resend-verification', { successMsg: `New code sent to ${row.email}` });
          reload();
        }}
        onVerify={(row) => doAction(row, 'verify', {
          confirm: `Mark ${row.email} as verified? They won't need the email code.`,
          successMsg: `${row.email} verified`,
        }).then(reload)}
      />

      {/* Operator cockpit — real metrics only (MRR/revenue cards return once Stripe is wired) */}
      <section>
        <h2 className="overline text-xs text-slate-500 mb-2">Growth</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total tenants', value: data.total_tenants, icon: Users, c: 'text-slate-900' },
            { label: 'Signups · 7 d', value: data.signups_last_7d, icon: TrendingUp, c: 'text-emerald-600' },
            { label: 'Signups · 30 d', value: data.signups_last_30d, icon: TrendingUp, c: 'text-emerald-600' },
            { label: 'Signups · 90 d', value: data.signups_last_90d ?? 0, icon: TrendingUp, c: 'text-emerald-600' },
          ].map(({ label, value, icon: Icon, c }) => (
            <Card key={label} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="overline text-xs">{label}</p>
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <p className={`text-2xl font-heading font-bold mt-1 ${c}`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="overline text-xs text-slate-500 mb-2">Health</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Verified', value: data.verified, icon: UserCheck, c: 'text-emerald-600' },
            { label: 'Pending verify', value: data.pending_verification, icon: Clock, c: data.pending_verification ? 'text-amber-600' : 'text-slate-400' },
            { label: 'Suspended', value: data.suspended ?? 0, icon: UserX, c: (data.suspended ?? 0) > 0 ? 'text-rose-600' : 'text-slate-400' },
            { label: 'Churn · 30 d', value: `${data.churn_rate_pct ?? 0}%`, icon: AlertOctagon, c: (data.churn_rate_pct ?? 0) > 20 ? 'text-rose-600' : 'text-slate-600' },
          ].map(({ label, value, icon: Icon, c }) => (
            <Card key={label} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="overline text-xs">{label}</p>
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <p className={`text-2xl font-heading font-bold mt-1 ${c}`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="overline text-xs text-slate-500 mb-2">Usage</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Active · 7 d', value: data.active_last_7d ?? 0, icon: Activity, c: 'text-emerald-600' },
            { label: 'Active · 30 d', value: data.active_last_30d ?? 0, icon: Activity, c: 'text-emerald-600' },
            { label: 'Paying-fit tenants', value: data.active_tenants ?? 0, icon: Zap, c: 'text-indigo-600', hint: 'verified + 1+ vehicle' },
            { label: 'Total vehicles', value: data.total_vehicles, icon: Car, c: 'text-indigo-600',
              hint: data.avg_vehicles_per_tenant != null ? `${data.avg_vehicles_per_tenant} avg/tenant` : null },
          ].map(({ label, value, icon: Icon, c, hint }) => (
            <Card key={label} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="overline text-xs">{label}</p>
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <p className={`text-2xl font-heading font-bold mt-1 ${c}`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</p>
                {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <div className="rounded-md border border-dashed border-slate-200 p-3 text-xs text-slate-500 flex items-center gap-2">
        <Zap className="w-4 h-4 text-slate-400" />
        MRR, revenue and plan-breakdown cards will unlock once Stripe billing is connected.
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search className="w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by email, company or name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="subscribers-search"
        />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase text-xs">
              <tr>
                {[
                  ['email', 'Email'],
                  ['company_name', 'Company'],
                  ['name', 'Owner'],
                  ['created_at', 'Signed Up'],
                  ['trial_ends_at', 'Trial Ends'],
                  ['email_verified', 'Status'],
                  ['vehicles', '# Veh'],
                  ['team_size', 'Team'],
                  ['last_activity', 'Last Activity'],
                ].map(([col, label]) => (
                  <th
                    key={col}
                    onClick={() => toggleSort(col)}
                    className="text-left px-3 py-2 cursor-pointer hover:text-slate-900 whitespace-nowrap"
                    data-testid={`subscribers-th-${col}`}
                  >
                    {label}{sort.col === col ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                  </th>
                ))}
                <th className="text-right px-3 py-2 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="10" className="text-center py-10 text-slate-400">No subscribers match this filter.</td></tr>
              )}
              {rows.map((r) => {
                const busy = (action) => actingRow === `${r.id}:${action}`;
                return (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${r.suspended ? 'bg-rose-50/40' : ''}`} data-testid={`sub-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{r.email}</td>
                    <td className="px-3 py-2 text-slate-700">{r.company_name || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.name || '—'}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className={`px-3 py-2 whitespace-nowrap ${r.trial_ends_at && new Date(r.trial_ends_at) < new Date() ? 'text-red-600' : 'text-slate-500'}`}
                      data-testid={`subscriber-trial-ends-${r.id}`}>{fmtDate(r.trial_ends_at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap space-x-1">
                      {r.suspended && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">suspended</span>
                      )}
                      {!r.suspended && (r.email_verified ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">verified</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">pending</span>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right font-data tabular-nums">{r.vehicles}</td>
                    <td className="px-3 py-2 text-right font-data tabular-nums">{r.team_size}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.last_activity)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" title="Log in as this client"
                                disabled={busy('impersonate') || r.suspended}
                                onClick={() => impersonate(r)}
                                data-testid={`impersonate-${r.id}`}>
                          {busy('impersonate') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                        </Button>
                        {!r.email_verified && (
                          <Button size="sm" variant="outline" title="Resend verification email"
                                  disabled={busy('resend-verification')}
                                  onClick={() => resendVerify(r)}
                                  data-testid={`resend-verify-${r.id}`}>
                            {busy('resend-verification') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailCheck className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" title="Email a password reset code"
                                disabled={busy('reset-password')}
                                onClick={() => resetPassword(r)}
                                data-testid={`reset-pw-${r.id}`}>
                          {busy('reset-password') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                        </Button>
                        {r.suspended ? (
                          <Button size="sm" variant="outline" title="Reactivate" className="text-emerald-700 hover:bg-emerald-50"
                                  disabled={busy('reactivate')}
                                  onClick={() => reactivate(r)}
                                  data-testid={`reactivate-${r.id}`}>
                            {busy('reactivate') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" title="Suspend" className="text-rose-700 hover:bg-rose-50"
                                  disabled={busy('suspend')}
                                  onClick={() => suspend(r)}
                                  data-testid={`suspend-${r.id}`}>
                            {busy('suspend') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" title="Delete tenant permanently"
                                className="text-rose-700 hover:bg-rose-50 border-rose-200"
                                onClick={() => { setDeleteTarget(r); setDeleteConfirm(''); }}
                                data-testid={`delete-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Delete-tenant confirmation modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirm(''); } }}>
        <DialogContent className="max-w-md" data-testid="delete-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Permanently delete tenant
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2 mt-2">
                <p>
                  You are about to permanently delete <strong>{deleteTarget?.email}</strong> and every record they own:
                  vehicles, cost entries, drivers, suppliers, incidents, trips, documents and all team members.
                </p>
                <p className="text-rose-600 font-medium">This cannot be undone.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label className="text-sm">Type <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">{deleteTarget?.email}</code> to confirm</Label>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={deleteTarget?.email}
              autoFocus
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(''); }}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              disabled={deleting || deleteConfirm.trim().toLowerCase() !== (deleteTarget?.email || '').toLowerCase()}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscribersPage;
